import { IAttributeOptions, ILooseObject } from '@/src/types/common.interface';

export enum testCasesStatus {
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  RUNNING = 'RUNNING'
}

interface IMvelEditorConfiguration {
  TEST_CASE_STATUS: typeof testCasesStatus;
  MVEL_INPUT_SCHEMA: ILooseObject;
  MVEL_OUTPUT_SCHEMA: ILooseObject;
  MVEL_EDITOR_DEFAULT_SCRIPT: string;
  HEADER_TABS: IAttributeOptions[];
}

export const mvelEditorConfiguration: IMvelEditorConfiguration = {
  TEST_CASE_STATUS: testCasesStatus,
  MVEL_EDITOR_DEFAULT_SCRIPT: `// PBAC Policy Example - User Access Control using proper MVEL syntax

isAllowed = true;
regionRequested = webRequest.body.region;
regionAllowed = data.metadata.attributes.region;

// Loop over requested list
for (region : regionRequested) {
    if (!regionAllowed.contains(region)) {
        isAllowed = false
    }
}

return {
    "isAllowed" : isAllowed
};`,
  MVEL_INPUT_SCHEMA: {
    type: 'object',
    description: 'Root input object available inside the script',

    properties: {
      webRequest: {
        type: 'object',
        description: 'Incoming HTTP request details',
        example: 'webRequest.method == "POST"',

        properties: {
          method: {
            type: 'string',
            description:
              'HTTP method used for the request (get | post | put | delete | patch | options)',
            example: 'webRequest.method == "POST"'
          },
          body: {
            type: 'object',
            description: 'Request payload body. Accepts any valid JSON object',
            example: 'webRequest.body.amount > 1000'
          },
          headers: {
            type: 'object',
            description: 'Request headers as key-value pairs',
            example: 'webRequest.headers.authorization'
          },
          parameters: {
            type: 'object',
            description: 'Query parameters passed in the request URL',
            example: 'webRequest.parameters.source == "mobile"'
          },
          authHeader: {
            type: 'object',
            description: 'Authentication related information extracted from headers',

            properties: {
              bearerToken: {
                type: 'string',
                description: 'Bearer token value if present',
                example: 'authHeader.bearerToken != null'
              },
              apiKey: {
                type: 'string',
                description: 'API key value if API key based authentication is used',
                example: 'authHeader.apiKey == "abc-123"'
              },
              isAuthHeaderPresent: {
                type: 'boolean',
                description: 'Indicates whether any authentication header is present',
                example: '!authHeader.isAuthHeaderPresent'
              }
            }
          },
          uri: {
            type: 'string',
            description: 'Request URI path (without host and protocol)',
            example: 'webRequest.uri.startsWith("/auth_service")'
          }
        }
      },

      data: {
        type: 'object',
        description: 'Business or request-specific data payload',

        properties: {
          metadata: {
            type: 'object',
            description: 'Additional metadata associated with the request',

            properties: {
              attributes: {
                type: 'object',
                description: 'Dynamic attributes used for policy evaluation',

                properties: {
                  legal_entities: {
                    type: 'array',
                    description: 'List of legal entity identifiers',
                    example: 'data.metadata.attributes.legal_entities.contains("GST123")',
                    items: { type: 'string' }
                  }
                }
              }
            }
          }
        }
      },

      appKey: {
        type: 'string',
        description: 'Unique application key identifying the calling application',
        example: 'appKey == "inventory-app"'
      },

      orgKeyList: {
        type: 'array',
        description: 'List of organization keys associated with the request',
        example: 'orgKeyList.contains("org-123")',
        items: { type: 'string' }
      },

      apiConfig: {
        type: 'object',
        description: 'API configuration and authorization details',

        properties: {
          isAuthorized: {
            type: 'boolean',
            description: 'Indicates whether the request is authorized',
            example: '!apiConfig.isAuthorized'
          },
          method: {
            type: 'string',
            description: 'HTTP method supported by the API',
            example: 'apiConfig.method == "POST"'
          },
          version: {
            type: 'number',
            description: 'API configuration version',
            example: 'apiConfig.version >= 1'
          },
          policy: {
            type: 'object',
            description: 'Policy attached to the API',

            properties: {
              policyKey: {
                type: 'string',
                description: 'Unique policy identifier'
              },
              policyName: {
                type: 'string',
                description: 'Policy name'
              }
            }
          }
        }
      },

      policyData: {
        type: ['object', 'null'],
        description: 'Policy execution result or intermediate policy data',
        example: 'policyData != null'
      }
    },

    required: ['user', 'resource', 'action']
  },
  MVEL_OUTPUT_SCHEMA: {
    title: 'AuthorizationContext',
    description:
      'Authorization context containing web request details, identity, and configuration',
    type: 'object',
    properties: {
      webRequestDTO: {
        type: 'object',
        description: 'Web request details and metadata'
      },
      filters: {
        type: 'object',
        description: 'Map of filter criteria',
        additionalProperties: true
      },
      isAllowed: {
        type: 'boolean',
        description: 'Whether the authorization is allowed'
      },
      ex: {
        type: 'object',
        description: 'Exception details if any occurred during authorization',
        properties: {
          message: {
            type: 'string'
          },
          type: {
            type: 'string'
          }
        }
      },
      identity: {
        type: 'object',
        description: 'User identity information'
      },
      baseData: {
        type: 'object',
        description: 'Base user data from cache/storage'
      },
      apiConfig: {
        type: 'object',
        description: 'API configuration details'
      }
    },
    required: ['isAllowed'],
    additionalProperties: false
  },
  HEADER_TABS: [
    {
      label: 'Params',
      value: 'params'
    },
    {
      label: 'Headers',
      value: 'headers'
    },
    {
      label: 'Body',
      value: 'body'
    }
  ]
};
